/** Sélection textuelle résolue (document light DOM ou shadow root). */
export type ResolvedTextSelection = {
  selection: Selection;
  range: Range;
  text: string;
};

/** Parcourt récursivement les shadow roots ouverts sous un nœud racine. */
export function collectOpenShadowRoots(root: ParentNode): ShadowRoot[] {
  const found: ShadowRoot[] = [];
  const seen = new Set<ShadowRoot>();

  const visitElement = (el: Element) => {
    const shadow = el.shadowRoot;
    if (shadow && !seen.has(shadow)) {
      seen.add(shadow);
      found.push(shadow);
      visitShadow(shadow);
    }
    for (const child of el.children) visitElement(child);
  };

  const visitShadow = (shadow: ShadowRoot) => {
    for (const child of shadow.children) {
      if (child instanceof Element) visitElement(child);
    }
  };

  if (root instanceof Document) {
    if (root.documentElement) visitElement(root.documentElement);
    return found;
  }

  if (root instanceof ShadowRoot) {
    visitShadow(root);
    return found;
  }

  if (root instanceof Element) visitElement(root);
  return found;
}

function selectionText(sel: Selection | null | undefined): string {
  return sel?.toString().trim() ?? '';
}

function pickBestSelection(candidates: Selection[]): ResolvedTextSelection | null {
  let best: ResolvedTextSelection | null = null;
  for (const selection of candidates) {
    if (selection.rangeCount === 0) continue;
    const text = selectionText(selection);
    if (text.length < 2) continue;
    if (!best || text.length > best.text.length) {
      best = { selection, range: selection.getRangeAt(0), text };
    }
  }
  return best;
}

/**
 * Retourne la sélection active, y compris dans les shadow roots (landing `/`).
 * `window.getSelection()` seul ne suffit pas lorsque le contenu est isolé en shadow DOM.
 */
export function resolveComposedTextSelection(): ResolvedTextSelection | null {
  const candidates: Selection[] = [];
  const docSel = window.getSelection();
  if (docSel) candidates.push(docSel);

  for (const shadow of collectOpenShadowRoots(document)) {
    const shadowSel = shadow.getSelection?.();
    if (shadowSel) candidates.push(shadowSel);
  }

  return pickBestSelection(candidates);
}

export function selectionIsInsideExcludedZone(selection: Selection): boolean {
  if (selection.rangeCount === 0) return true;
  const node = selection.getRangeAt(0).commonAncestorContainer;
  const el = (node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement) as HTMLElement | null;
  if (!el) return true;
  if (el.closest('[data-a11y-no-speak]')) return true;
  if (el.closest('[data-a11y-panel]')) return true;
  if (el.closest('[data-a11y-voice-guide]')) return true;
  if (el.closest('input,textarea,select,[contenteditable="true"]')) return true;
  return false;
}

export function getRangeClientRect(range: Range): DOMRect | null {
  const rect = range.getBoundingClientRect();
  if (rect.width > 0 || rect.height > 0) return rect;

  const rects = range.getClientRects();
  if (rects.length === 0) return null;

  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const r of rects) {
    if (r.width === 0 && r.height === 0) continue;
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (!Number.isFinite(top)) return null;
  return new DOMRect(left, top, Math.max(0, right - left), Math.max(0, bottom - top));
}
